<template>
  <ListView
    title="Account Deletion Requests"
    :isFluid="true"
    @reload="reload"
    :search.sync="search"
    :data="data"
    :page.sync="page"
    :header="headers"
    :isLoading="isLoading"
  >
    <template v-slot:filter>
      <div class="d-flex align-center mb-4" style="gap: 12px">
        <div style="width: 14rem">
          <fletrix-select
            v-model="selectedFilter"
            :items="filterOptions"
            item-text="label"
            item-value="value"
            label="Status Filter"
            hide-details="auto"
            solo
          />
        </div>
      </div>
    </template>

    <template v-slot:item.member="{ item }">
      <span v-if="item.member">
        {{ item.member.name_last }} {{ item.member.name_first }}
        <span class="grey--text ml-1">({{ item.member.phone }})</span>
      </span>
      <span v-else>-</span>
    </template>

    <template v-slot:item.created_at="{ item }">
      {{ formatDate(item.created_at) }}
    </template>

    <template v-slot:item.status="{ item }">
      <v-chip small :color="statusColor(item.status)" text-color="white">
        {{ item.status }}
      </v-chip>
    </template>

    <template v-slot:item.reviewedBy="{ item }">
      {{ item.reviewedBy ? item.reviewedBy.name : '-' }}
    </template>

    <template v-slot:item.reviewedAt="{ item }">
      {{ item.reviewedAt ? formatDate(item.reviewedAt) : '-' }}
    </template>

    <template v-slot:item.actions="{ item }">
      <template v-if="item.status === 'pending'">
        <v-icon
          small
          class="mr-2"
          color="success"
          title="Approve"
          @click="handleUpdateStatus(item, 'approved')"
        >
          mdi-check
        </v-icon>
        <v-icon
          small
          color="error"
          title="Reject"
          @click="handleUpdateStatus(item, 'rejected')"
        >
          mdi-close
        </v-icon>
      </template>
    </template>
  </ListView>
</template>

<script>
import listViewPageMixin from '@/services/listViewPageMixin';
import api from '@/api';
import { getList, updateStatus } from '@/api/accountDeletionRequests';
import { FletrixSelect } from '@/components/Atom';

export default {
  name: 'AccountDeletionRequestList',
  mixins: [listViewPageMixin],
  components: { FletrixSelect },
  data() {
    return {
      selectedFilter: 'pending',
      filterOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Handled', value: 'handled' },
        { label: 'All', value: 'all' },
      ],
    };
  },
  computed: {
    title: () => 'Account Deletion Requests',
    restfulURL: () => '/rml/admin/AccountDeletionRequests',
    headers() {
      return [
        { text: 'Member', value: 'member', sortable: false },
        { text: 'Submitted At', value: 'created_at' },
        { text: 'Reason', value: 'reason', sortable: false },
        { text: 'Status', value: 'status' },
        { text: 'Reviewed By', value: 'reviewedBy', sortable: false },
        { text: 'Reviewed At', value: 'reviewedAt' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ];
    },
    filter() {
      if (this.selectedFilter === 'pending') return { status: 'pending' };
      if (this.selectedFilter === 'handled') return { status: { $in: ['approved', 'rejected'] } };
      return {};
    },
  },
  methods: {
    async load() {
      this.data = await getList(api, {
        page: this.page,
        search: this.search,
        limit: this.limit,
        sort: this.sort,
        filter: this.computedFilter,
        populate: 'member',
      });
    },

    async handleUpdateStatus(item, status) {
      const label = status === 'approved' ? 'Approve' : 'Reject';
      const confirmMessage =
        status === 'approved'
          ? "This action is irreversible. Are you sure you want to approve this request?"
          : 'Are you sure you want to reject this account deletion request?';

      const confirmed = await this.$root.$confirm.open(
        `${label} Request`,
        confirmMessage,
        { color: status === 'approved' ? 'green' : 'red' },
      );
      if (!confirmed) return;

      try {
        await updateStatus(api, item._id, status);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: `Request ${status} successfully`,
          type: 'success',
          color: 'success',
        });
        await this.reload();
      } catch (e) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: e,
          type: 'error',
        });
      }
    },

    statusColor(status) {
      const map = { pending: 'orange', approved: 'green', rejected: 'red' };
      return map[status] || 'grey';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    },
  },
};
</script>
